/* @flow */

import type { Customer, TransferableMember } from "shop-state/types";
import type { FormData } from "@awardit/formaggio/src/types";

import React, { useEffect, useState, useContext } from "react";
import { Helmet } from "react-helmet-async";
import { Dropdown, DropdownItem } from "@crossroads/ui-components";
import { Form, rules, isRequired, isNumeric } from "@awardit/formaggio";
import { useSendMessage } from "crustate/react";
import { StoreInfoContext, useClient } from "entrypoint/shared";
import Wrapper from "components/Wrapper";
import Field from "components/Field";
import Button from "components/Button";
import { BoxBody, Box } from "components/Box";
import Back from "components/AccountView/Back";
import Header from "components/AccountView/Header";
import useFormat from "helpers/use-format";
import { numberGt, numberLt } from "helpers/validation";
import YourPoints from "components/AccountView/your-points";
import ExpiringPoints from "components/AccountView/expiring-points";
import { syncCustomer } from "@crossroads/shop-state/customer";
import { customer as customerQuery, listTransferableMembers, transferMemberPoints } from "queries";
import { addMessage } from "state/messages";
import { useTranslate } from "@awardit/react-use-translate";
import cn from "classnames";

import styles from "./styles.scss";

const PointsView = ({ customer }: { customer: Customer }): React$Node => {
  const sendMessage = useSendMessage();
  const t = useTranslate();
  const client = useClient();
  const { content: { pointsview } } = useContext(StoreInfoContext);
  const [members, setMembers] = useState<Array<TransferableMember>>([]);
  const [loading, setIsLoading] = useState<boolean>(false);
  const { formatPoints } = useFormat(false);
  const hasExpiringPoints = Boolean(customer &&
    customer.memberPoints &&
    customer.memberPoints.expirePoints &&
    customer.memberPoints.expirePoints.length > 0);

  const getMemberList = async () => {
    await client(listTransferableMembers).then(({ listTransferableMembers }) => {
      if (listTransferableMembers !== null && listTransferableMembers.result === "SUCCESS") {
        setMembers(listTransferableMembers.data.filter(member => !member.isCompany));
      }
      else {
        sendMessage(addMessage("GET_TRANSFERABLE_MEMBERS_ERROR", "error"));
      }
    });
  };

  useEffect(() => {
    getMemberList();
  }, [client]);

  const defaultState = {
    toMember: "",
    fromMember: "",
    points: 0,
  };
  const [state, setState] = useState<FormData>(defaultState);
  const fromMember = members.find(a => a.memberuserid === state.fromMember) || {};

  const validation = rules([
    isRequired("fromMember"),
    isRequired("toMember"),
    isRequired("points"),
    isNumeric("points"),
    numberGt("points", 0),
    numberLt("points", fromMember.balance ? fromMember.balance + 1 : 0),
  ]);

  const errors = validation(state);

  const submit = async e => {
    e.preventDefault();
    setIsLoading(true);

    if (!showTransfer) {
      setIsLoading(false);
      return;
    }

    if (customer) {
      const res = await client(transferMemberPoints, {
        fromMember: state.fromMember.toString(),
        toMembers: [state.toMember.toString()],
        points: Number(state.points),
        message: t("ACCOUNT.MEMBER_GROUP.TRANSFER_POINTS.DEFAULT_MESSAGE"),
      });

      if (res.transferMemberPoints !== null && res.transferMemberPoints.result === "SUCCESS") {
        await client(customerQuery).then(({ customer }) => {
          sendMessage(syncCustomer(customer));
          sendMessage(addMessage(`TRANSFER_MEMBER_POINTS_SUCCESS`, "success"));
          setIsLoading(false);
          getMemberList();
        });
      }
      else {
        sendMessage(addMessage("TRANSFER_MEMBER_POINTS_ERROR", "error"));
        setIsLoading(false);
      }
    }
  };

  const showTransfer = pointsview.toggle === true && Boolean(members && members.length > 1);

  return (
    <Wrapper variant="pageWrapper" className="awardit-pageWrapper awardit-pointsView">
      <Helmet title={pointsview.pageTitle ?? ""} />
      <Back />
      {pointsview.header &&
        <Header subHeader={pointsview.header} />
      }

      <div className={styles.container}>
        <div className={cn(styles.boxContainer, styles.left)}>
          <Box>
            <YourPoints customer={customer} />
          </Box>
          {hasExpiringPoints && (
            <Box>
              <ExpiringPoints customer={customer} />
            </Box>
          )}
        </div>

        {showTransfer &&
          <Box className={cn(styles.right, styles.transferPointsBox)}>
            <header className={styles.boxHeader}>
              {pointsview.transfer && (
                <h2 className={styles.boxHeading}>{pointsview.transfer}</h2>
              )}
              {pointsview.transferDescription &&
                <p>{pointsview.transferDescription}</p>
              }
            </header>
            <Form
              disabled={loading}
              errors={errors}
              value={state}
              onChange={x => setState({ ...state, ...x })}
              onSubmit={submit}
            >
              <BoxBody className={styles.removeBottomPadding}>
                <div className={styles.row}>
                  {showTransfer && (
                    <>
                      <div className={styles.s50}>
                        {pointsview.from &&
                          <label htmlFor="fromMember" className={styles.label}>{pointsview.from}</label>
                        }
                        <Dropdown
                          className={styles.selector}
                          name="fromMember"
                          value={state.fromMember ? state.fromMember.toString() : ""}
                          placeholder={pointsview.selectAccount ?? ""}
                          onChange={valueFrom => {
                            const fromMemberVal = valueFrom !== null ?
                              valueFrom.toString() : "";
                            setState({ ...state, fromMember: fromMemberVal });
                          }}
                        >
                          {members.map(a => (
                            <DropdownItem key={`from_${a.memberuserid}`} value={a.memberuserid}>
                              {`${a.memberuserid} - ${pointsview.amount ?? ""} \n ${formatPoints(a.balance)}`}
                            </DropdownItem>
                          ))}
                        </Dropdown>
                      </div>
                      <div className={styles.s50}>
                        {pointsview.to &&
                          <label htmlFor="toMember" className={styles.label}>{pointsview.to}</label>
                        }
                        <Dropdown
                          className={styles.selector}
                          name="toMember"
                          value={state.toMember ? state.toMember.toString() : ""}
                          placeholder={pointsview.selectAccount ?? ""}
                          onChange={valueTo => {
                            const toMemberVal = valueTo !== null ?
                              valueTo.toString() : "";

                            setState({ ...state, toMember: toMemberVal });
                          }}
                        >
                          {members.map(a => (
                            <DropdownItem key={`from_${a.memberuserid}`} value={a.memberuserid}>
                              {`${a.memberuserid} - ${pointsview.amount ?? ""} \n ${formatPoints(a.balance)}`}
                            </DropdownItem>
                          ))}
                        </Dropdown>
                      </div>
                    </>
                  )}
                </div>
                <div className={styles.row}>
                  {pointsview.pointsAmount &&
                    <div className={styles.s50}>
                      <Field name="points" label={pointsview.pointsAmount} />
                    </div>
                  }
                  <div className={styles.s50}>
                    <div className={styles.spacer} />
                    {pointsview.transferButton &&
                      <Button
                        type="submit"
                        variant="primary"
                        loading={loading}
                        className={styles.button}
                      >
                        <span>{pointsview.transferButton}</span>
                      </Button>
                    }
                  </div>
                </div>
              </BoxBody>
            </Form>
          </Box>
        }
      </div>
    </Wrapper>
  );
};

export default PointsView;
